import { useEffect, useState } from "react";
import {
  Plus, Trash2, Loader2, Save, Lock, KeyRound,
  MessageCircle, Mail, Phone, Instagram, Facebook, Globe,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/lib/api";

const KIND_OPTIONS = [
  { value: "whatsapp", label: "WhatsApp", icon: MessageCircle, placeholder: "5519999999999 (com DDI + DDD)" },
  { value: "email", label: "E-mail", icon: Mail, placeholder: "contato@exemplo.com" },
  { value: "phone", label: "Telefone", icon: Phone, placeholder: "(19) 99999-9999" },
  { value: "instagram", label: "Instagram", icon: Instagram, placeholder: "falaitapira (sem o @)" },
  { value: "facebook", label: "Facebook", icon: Facebook, placeholder: "falaitapira ou URL completa" },
  { value: "url", label: "Site / URL", icon: Globe, placeholder: "https://exemplo.com" },
  { value: "other", label: "Outro", icon: Globe, placeholder: "Valor livre" },
];

const kindMeta = (kind) => KIND_OPTIONS.find((k) => k.value === kind) || KIND_OPTIONS[0];

const empty = { kind: "whatsapp", label: "", value: "", active: true };

const AdminSettings = () => {
  const [contacts, setContacts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwdBusy, setPwdBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await adminApi.get("/admin/settings/contacts");
      setContacts(data);
    } catch {}
  };

  useEffect(() => {
    load();
  }, []);

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({ kind: c.kind, label: c.label, value: c.value, active: c.active });
  };

  const resetForm = () => {
    setForm(empty);
    setEditingId(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.label.trim() || !form.value.trim()) {
      toast.error("Preencha rótulo e valor do contato.");
      return;
    }
    setBusy(true);
    try {
      if (editingId) {
        await adminApi.patch(`/admin/settings/contacts/${editingId}`, form);
        toast.success("Contato atualizado.");
      } else {
        await adminApi.post("/admin/settings/contacts", form);
        toast.success("Contato adicionado.");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (id) => {
    try {
      await adminApi.post(`/admin/settings/contacts/${id}/toggle`);
      load();
    } catch {}
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este contato do Painel do Cidadão?")) return;
    try {
      await adminApi.delete(`/admin/settings/contacts/${id}`);
      toast.success("Removido.");
      if (editingId === id) resetForm();
      load();
    } catch {}
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) {
      toast.error("As senhas novas não coincidem.");
      return;
    }
    if (pwd.new_password.length < 8) {
      toast.error("A nova senha precisa ter no mínimo 8 caracteres.");
      return;
    }
    setPwdBusy(true);
    try {
      await adminApi.post("/admin/settings/change-password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      toast.success("Senha atualizada com sucesso.");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setPwdBusy(false);
    }
  };

  const meta = kindMeta(form.kind);

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.28em] text-[var(--fi-red)] font-bold mb-1">
          Painel do Cidadão
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase text-[var(--fi-blue)] leading-tight">
          Configurações
        </h1>
        <p className="text-gray-500 mt-1">
          Contatos exibidos no Painel do Cidadão e credenciais do administrador.
        </p>
      </div>

      {/* Contact form */}
      <section className="rounded-xl bg-white p-5 shadow-[0_8px_30px_rgba(13,27,75,0.06)]">
        <h2 className="font-display text-xl font-extrabold uppercase text-[var(--fi-blue)] mb-3">
          {editingId ? "Editar contato" : "Novo contato"}
        </h2>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-[var(--fi-blue)] mb-1.5">
              Tipo *
            </label>
            <select
              data-testid="admin-contact-kind"
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value })}
              className="input bg-white"
            >
              {KIND_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-[var(--fi-blue)] mb-1.5">
              Rótulo exibido *
            </label>
            <input
              data-testid="admin-contact-label"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              className="input"
              placeholder="Ex: WhatsApp da Redação"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs uppercase tracking-widest font-bold text-[var(--fi-blue)] mb-1.5">
              Valor *
            </label>
            <input
              data-testid="admin-contact-value"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              className="input"
              placeholder={meta.placeholder}
            />
            <div className="text-xs text-gray-500 mt-1">
              Dica: WhatsApp use somente números (55 + DDD + número). Instagram sem o @.
            </div>
          </div>
          <div className="sm:col-span-2 flex items-center gap-2 pt-1">
            <button
              type="submit"
              data-testid="admin-contact-submit"
              disabled={busy}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[var(--fi-red)] hover:bg-[var(--fi-red-hover)] text-white font-bold uppercase tracking-widest text-xs disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : editingId ? <Save className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {editingId ? "Salvar alterações" : "Adicionar contato"}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="text-xs uppercase tracking-widest font-bold text-gray-500 hover:text-[var(--fi-blue)]"
              >
                Cancelar
              </button>
            )}
          </div>
        </form>
        <style>{`.input{width:100%;padding:10px 12px;border:1px solid #e5e7eb;border-radius:10px;font-size:14px;outline:none;transition:border-color .15s}.input:focus{border-color:#0D1B4B}`}</style>
      </section>

      {/* Contact list */}
      <section>
        <h2 className="font-display text-xl font-extrabold uppercase text-[var(--fi-blue)] mb-3">
          Contatos cadastrados
        </h2>
        <div className="space-y-2">
          {contacts.length === 0 && (
            <div className="rounded-xl bg-white p-6 text-center text-gray-400">
              Nenhum contato cadastrado.
            </div>
          )}
          {contacts.map((c) => {
            const M = kindMeta(c.kind);
            const Icon = M.icon;
            return (
              <div
                key={c.id}
                data-testid={`admin-contact-row-${c.id}`}
                className="rounded-xl bg-white p-4 shadow-[0_6px_20px_rgba(13,27,75,0.05)] flex items-center gap-3"
              >
                <Icon className="w-6 h-6 text-[var(--fi-blue)] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[var(--fi-blue)] truncate">{c.label}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {M.label} · {c.value}
                  </div>
                </div>
                <button
                  onClick={() => toggle(c.id)}
                  data-testid={`admin-contact-toggle-${c.id}`}
                  className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest font-bold transition-colors ${
                    c.active
                      ? "bg-emerald-500 text-white hover:bg-emerald-600"
                      : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                  }`}
                >
                  {c.active ? "Ativo" : "Oculto"}
                </button>
                <button
                  onClick={() => startEdit(c)}
                  data-testid={`admin-contact-edit-${c.id}`}
                  className="px-3 py-1.5 rounded-lg text-xs uppercase tracking-widest font-bold text-[var(--fi-blue)] hover:bg-gray-100"
                >
                  Editar
                </button>
                <button
                  onClick={() => remove(c.id)}
                  data-testid={`admin-contact-delete-${c.id}`}
                  className="w-9 h-9 rounded-lg text-gray-400 hover:text-[var(--fi-red)] hover:bg-red-50 flex items-center justify-center transition-colors"
                  aria-label="Remover"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Change password */}
      <section className="rounded-xl bg-white p-5 shadow-[0_8px_30px_rgba(13,27,75,0.06)]">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound className="w-5 h-5 text-[var(--fi-red)]" />
          <h2 className="font-display text-xl font-extrabold uppercase text-[var(--fi-blue)]">
            Alterar senha do administrador
          </h2>
        </div>
        <form onSubmit={changePassword} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <PwdField
            label="Senha atual *"
            testId="admin-pwd-current"
            value={pwd.current_password}
            onChange={(v) => setPwd({ ...pwd, current_password: v })}
          />
          <PwdField
            label="Nova senha *"
            testId="admin-pwd-new"
            value={pwd.new_password}
            onChange={(v) => setPwd({ ...pwd, new_password: v })}
          />
          <PwdField
            label="Confirmar nova senha *"
            testId="admin-pwd-confirm"
            value={pwd.confirm}
            onChange={(v) => setPwd({ ...pwd, confirm: v })}
          />
          <div className="sm:col-span-3">
            <button
              type="submit"
              data-testid="admin-pwd-submit"
              disabled={pwdBusy}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[var(--fi-blue)] hover:bg-[var(--fi-blue-2)] text-white font-bold uppercase tracking-widest text-xs disabled:opacity-60"
            >
              {pwdBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Atualizar senha
            </button>
            <div className="text-xs text-gray-500 mt-2">
              Mínimo 8 caracteres. Use uma senha forte e guarde em local seguro — não há recuperação automática.
            </div>
          </div>
        </form>
        <style>{`.input{width:100%;padding:10px 12px;border:1px solid #e5e7eb;border-radius:10px;font-size:14px;outline:none;transition:border-color .15s}.input:focus{border-color:#0D1B4B}`}</style>
      </section>
    </div>
  );
};

const PwdField = ({ label, testId, value, onChange }) => (
  <div>
    <label className="block text-xs uppercase tracking-widest font-bold text-[var(--fi-blue)] mb-1.5">
      {label}
    </label>
    <input
      data-testid={testId}
      type="password"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="input"
      autoComplete="new-password"
    />
  </div>
);

export default AdminSettings;
